import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import GeneralDropdownsTriggersTriggerPowerSelectMeatballs from 'lumen/components/general/dropdowns/triggers/trigger-power-select-meatballs';
import can from 'lumen/helpers/can';
import { ISSUE_TYPE_PRETTY_NAMES } from 'lumen/models/parity-manager/issue';
import { MAIL_TYPE } from 'lumen/utils/constants/pm-constant-values';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';

const MAPPING_DROPDOWN_OPTIONS = {
  DELETE: 'delete',
  EXTRANET: 'extranet',
  LIVE_VIEW: 'live-view',
  SCREEN_SHOTS: 'screenshots',
  CONTACT_WHOLESALER: 'contact-wholesaler',
  DOWNLOAD_ATTACHMENTS: 'download-attachments',
  ATTACH_FILES: 'attach-files',
};

interface GeneralTablesCellsTdWithActionsSignature {
  Args: {
    cellData: unknown;
    row: unknown;
  };
}

export default class GeneralTablesCellsTdWithActions extends Component<GeneralTablesCellsTdWithActionsSignature> {
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked currentMenuId = null;
  @tracked emberPowerSelect = null;
  @tracked isEmberPowerSelectOpen = false;
  @tracked isOverDropdownMenu = false;

  dropdownMenu = null;
  rowMenuMouseEnterHandler = null;
  rowMenuMouseLeaveHandler = null;
  trElement = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baitAndSwitchActions() {
    const dropdownOptions = [];

    // @ts-expect-error: Incorrect type
    if (this.issue.isManualEntry) {
      dropdownOptions.push({
        id: MAPPING_DROPDOWN_OPTIONS.DELETE,
        icon: 'bin',
        label: this.intl.t('general.delete'),
      });
    }

    dropdownOptions.push({
      id: MAPPING_DROPDOWN_OPTIONS.DOWNLOAD_ATTACHMENTS,
      icon: 'arrow-down',
      label: this.intl.t('parity_manager.download_attachments'),
    });

    return dropdownOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dropdownOptions() {
    // @ts-expect-error: Incorrect type
    if (this.issue.isMajorOTAIssue) {
      return this.majorOtaActions;
    }
    // @ts-expect-error: Incorrect type
    if (this.issue.isTestBookingIssue) {
      return this.testBookingActions;
    }
    // @ts-expect-error: Incorrect type
    if (this.issue.isBaitAndSwitchIssue) {
      return this.baitAndSwitchActions;
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get issue() {
    return this.args.cellData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get majorOtaActions() {
    const dropdownOptions = [];

    // @ts-expect-error: Incorrect type
    if (this.issue.isManualEntry) {
      dropdownOptions.push({
        id: MAPPING_DROPDOWN_OPTIONS.DELETE,
        icon: 'bin',
        label: this.intl.t('general.delete'),
      });
    }

    // @ts-expect-error: Incorrect type
    if (this.issue.hasExtranetUrl) {
      dropdownOptions.push({
        id: MAPPING_DROPDOWN_OPTIONS.EXTRANET,
        icon: 'link-out',
        label: this.intl.t('parity.extranet_login'),
      });
    }

    // @ts-expect-error: Incorrect type
    if (this.issue.hasLiveViewUrl) {
      dropdownOptions.push({
        id: MAPPING_DROPDOWN_OPTIONS.LIVE_VIEW,
        icon: 'visibility-on',
        label: this.intl.t('parity_manager.visit_live_view'),
      });
    }

    dropdownOptions.push(
      {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        id: MAPPING_DROPDOWN_OPTIONS.SCREENSHOTS,
        icon: 'image-outline',
        label: this.intl.t('parity_manager.view_screenshots'),
      },
      {
        id: MAPPING_DROPDOWN_OPTIONS.ATTACH_FILES,
        icon: 'upload',
        label: this.intl.t('parity_manager.attach_files'),
      },
    );

    return dropdownOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get testBookingActions() {
    const dropdownOptions = [];

    // @ts-expect-error: Incorrect type
    if (this.issue.isManualEntry) {
      dropdownOptions.push({
        id: MAPPING_DROPDOWN_OPTIONS.DELETE,
        icon: 'bin',
        label: this.intl.t('general.delete'),
      });
    }

    // A wholesaler has to be selected in order to be able to send a mail
    // @ts-expect-error: Incorrect type
    if (this.issue.hasRootCause) {
      dropdownOptions.push({
        id: MAPPING_DROPDOWN_OPTIONS.CONTACT_WHOLESALER,
        icon: 'mail',
        label: this.intl.t('parity_manager.contact_wholesaler'),
      });
    }

    dropdownOptions.push(
      {
        id: MAPPING_DROPDOWN_OPTIONS.DOWNLOAD_ATTACHMENTS,
        icon: 'download',
        label: this.intl.t('parity_manager.download_attachments'),
      },
      {
        id: MAPPING_DROPDOWN_OPTIONS.ATTACH_FILES,
        icon: 'upload',
        label: this.intl.t('parity_manager.attach_files'),
      },
    );

    return dropdownOptions;
  }

  willDestroy(): void {
    super.willDestroy();

    this.emberPowerSelect = null;
    // unregister all listeners
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.trElement.removeEventListener(
      'mouseenter',
      this.rowMenuMouseEnterHandler,
    );
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.trElement.removeEventListener(
      'mouseleave',
      this.rowMenuMouseLeaveHandler,
    );
    this.trElement = null;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.dropdownMenu?.removeEventListener(
      'mouseenter',
      // @ts-expect-error: Incorrect type
      this.dropdownMenuMouseEnterHandler,
    );
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.dropdownMenu?.removeEventListener(
      'mouseleave',
      // @ts-expect-error: Incorrect type
      this.dropdownMenuMouseLeaveHandler,
    );
    this.dropdownMenu = null;
  }

  closePowerSelectDropdownMenu(): void {
    this.removeRowMenu();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.emberPowerSelect?.actions?.close();
  }

  removeRowMenu(): void {
    // @ts-expect-error: Incorrect type
    document
      // @ts-expect-error: Incorrect type
      .getElementById(this.currentMenuId)
      .classList.remove('show-actions-menu');
  }

  @action
  attachFiles(): void {
    this.trackIssueTableAction('File Attached');
    // @ts-expect-error: Incorrect type
    const { subscriptionId, id, issueType } = this.issue;
    this.onIssueUploadClicked(subscriptionId, id, issueType);
  }

  @action
  async deleteManualEntry(): Promise<void> {
    const confirmDeletion = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('parity_manager.confirm.delete_issue'),
      text: this.intl.t('general.are_you_sure'),
    });

    if (confirmDeletion) {
      const params = {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        subscription_id: this.issue.subscriptionId,
        market_id: this.hotelAndMarketService.market?.id,
      };

      const deleteURL = formatUrl(
        '/apigateway/v1/app/paritymanager/delete',
        // @ts-expect-error: Incorrect type
        params,
        this.ajax.host,
      );

      this.ajax
        .request(deleteURL, {
          method: 'POST',
          dataType: 'json',
          contentType: 'application/json; charset=utf-8',
          data: JSON.stringify({
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            issue_id: this.issue.id,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            issue_type: this.issue.issueType,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            subscription_id: this.issue.subscriptionId,
          }),
        })
        .then(() => {
          this.toast.success(
            this.intl.t('parity_manager.delete_success').toString(),
            {
              showIcon: true,
            },
          );
          this.router.refresh('market.parity.parity-manager');
        })
        .catch((error) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this.errorTracking.addBreadcrumb({ data: { error } });

          // Note: There are several specific error messages (e.g. custom Choice flow). Translation keys will be returned by the API
          // - "parity_manager.issue.update.error.cant_confirm_in_previous_violation_period"
          // - "parity_manager.issue.update.error.cant_confirm_in_cure_period"
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.alerting.showErrorAlert({
            text: getAPIErrorMessage(error, this.intl),
          });
        });
    }
  }

  @action
  exportPDF(): void {
    const url = formatUrl(
      '/apigateway/v1/app/paritymanager/export_issue',
      {
        // @ts-expect-error: Incorrect type
        market_id: this.hotelAndMarketService.market?.id,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        issue_type: this.issue.issueType,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        issue_id: this.issue.id,
      },
      this.ajax.host,
    );

    openUrl(url);
  }

  @action
  goToExtranet(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    openUrl(this.issue.extranetUrl);
  }

  @action
  goToLiveView(): void {
    this.trackIssueTableAction('Live View Opened');
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    openUrl(this.issue.liveUrl);
  }

  @action
  goToScreenshotsOrDownloadZip(): void {
    this.trackIssueTableAction('Screenshot Viewed');
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    openUrl(this.issue.screenshotsUrl);
  }

  @action
  onInsert(): void {
    // @ts-expect-error: Incorrect type
    const tdElement = document.getElementById(
      // @ts-expect-error: Incorrect type
      `cell-action-${this.args.row.id}`,
    ).parentElement;
    // @ts-expect-error: Incorrect type
    this.trElement = tdElement.parentElement;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const rowMenuMouseEnterHandler = () => {
      const query = `td#${tdElement?.id} > .cell-with-actions-container`;
      // @ts-expect-error: Incorrect type
      this.currentMenuId = document.querySelector(query).id;
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const dropdownMenuMouseEnterHandler = () => {
      this.isOverDropdownMenu = true;
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const dropdownMenuMouseLeaveHandler = () => {
      this.closePowerSelectDropdownMenu();
      // reset flags
      this.isEmberPowerSelectOpen = false;
      this.isOverDropdownMenu = false;
    };

    // @ts-expect-error: Incorrect type
    this.dropdownMenuMouseEnterHandler = dropdownMenuMouseEnterHandler;
    // @ts-expect-error: Incorrect type
    this.dropdownMenuMouseLeaveHandler = dropdownMenuMouseLeaveHandler;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const rowMenuMouseLeaveHandler = () => {
      // close dropdown and hide the row menu
      // @ts-expect-error: Incorrect type
      this.dropdownMenu = document.querySelector('.ember-power-select-options');

      setTimeout(() => {
        if (this.isEmberPowerSelectOpen && !this.isOverDropdownMenu) {
          this.closePowerSelectDropdownMenu();
        }
      }, 10);

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.dropdownMenu?.addEventListener(
        'mouseenter',
        // @ts-expect-error: Incorrect type
        this.dropdownMenuMouseEnterHandler,
      );
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.dropdownMenu?.addEventListener(
        'mouseleave',
        // @ts-expect-error: Incorrect type
        this.dropdownMenuMouseLeaveHandler,
      );
    };

    // @ts-expect-error: Incorrect type
    this.rowMenuMouseEnterHandler = rowMenuMouseEnterHandler;
    // @ts-expect-error: Incorrect type
    this.rowMenuMouseLeaveHandler = rowMenuMouseLeaveHandler;

    // Add hover listeners to the tr element to handle submenu actions.
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.trElement.addEventListener(
      'mouseenter',
      this.rowMenuMouseEnterHandler,
    );
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.trElement.addEventListener(
      'mouseleave',
      this.rowMenuMouseLeaveHandler,
    );
  }

  // eslint-disable-next-line max-params
  @action
  onIssueMailClicked(
    // @ts-expect-error: Incorrect type
    readyToSend,
    // @ts-expect-error: Incorrect type
    subscriptionId,
    // @ts-expect-error: Incorrect type
    issueId,
    // @ts-expect-error: Incorrect type
    issueType,
    // @ts-expect-error: Incorrect type
    mailType,
  ): void {
    // Check readyToSend flag first to be sure that the mail route can't be triggered in invalid cases
    if (readyToSend) {
      this.router.transitionTo('market.parity.parity-manager.mail', {
        queryParams: {
          subscriptionId,
          issueId,
          issueType,
          mailType,
        },
      });
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  onIssueUploadClicked(subscriptionId, issueId, issueType): void {
    this.router.transitionTo('market.parity.parity-manager.issueupload', {
      queryParams: {
        subscriptionId,
        issueId,
        issueType,
      },
    });
  }

  @action
  // @ts-expect-error: Incorrect type
  onOpen(emberPowerSelect): void {
    this.isEmberPowerSelectOpen = true;

    // set ember power select
    if (!this.emberPowerSelect) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.emberPowerSelect = emberPowerSelect;
    }

    // keep the selected row menu active when the mouse leaves the row and enters the dropdown menu
    const colMenuContainer = document.getElementById(
      // @ts-expect-error: Incorrect type
      `cell-action-${this.args.row.id}`,
    );
    // @ts-expect-error: Incorrect type
    colMenuContainer.classList.add('show-actions-menu');
  }

  @action
  // @ts-expect-error: Incorrect type
  setDropdownOption({ id: eventId }): void {
    if (eventId === MAPPING_DROPDOWN_OPTIONS.DELETE) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.deleteManualEntry();
    } else if (eventId === MAPPING_DROPDOWN_OPTIONS.EXTRANET) {
      this.goToExtranet();
    } else if (eventId === MAPPING_DROPDOWN_OPTIONS.LIVE_VIEW) {
      this.goToLiveView();
      // @ts-expect-error: Incorrect type
    } else if (eventId === MAPPING_DROPDOWN_OPTIONS.SCREENSHOTS) {
      this.goToScreenshotsOrDownloadZip();
    } else if (eventId === MAPPING_DROPDOWN_OPTIONS.CONTACT_WHOLESALER) {
      this.transitionToWholesalerMail();
    } else if (eventId === MAPPING_DROPDOWN_OPTIONS.DOWNLOAD_ATTACHMENTS) {
      this.goToScreenshotsOrDownloadZip();
    } else if (eventId === MAPPING_DROPDOWN_OPTIONS.ATTACH_FILES) {
      this.attachFiles();
    }

    // close the row's menu after selection
    this.removeRowMenu();
  }

  @action
  // @ts-expect-error: Incorrect type
  trackIssueTableAction(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.usageAnalytics.track(event, {
      eventTrigger: 'issue-table',
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      viewType: ISSUE_TYPE_PRETTY_NAMES[this.issue.issueType],
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      channelName: this.issue.formattedChannel,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      daysSinceShopped: this.issue.daysSinceShopped,
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  transitionToHotelMail() {
    // @ts-expect-error: Incorrect type
    const { subscriptionId, id, issueType } = this.issue;
    this.trackIssueTableAction('Hotel Contacted');
    return this.onIssueMailClicked(
      true,
      subscriptionId,
      id,
      issueType,
      MAIL_TYPE.HOTEL,
    );
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  transitionToWholesalerMail() {
    // @ts-expect-error: Incorrect type
    const { subscriptionId, id, issueType } = this.issue;
    return this.onIssueMailClicked(
      true,
      subscriptionId,
      id,
      issueType,
      MAIL_TYPE.WHOLESALER,
    );
  }

  <template>
    <div
      class="cell-with-actions-container stop-propagation-to-drawer gap-200 pr-300"
      {{! @glint-expect-error: Incorrect type }}
      id="cell-action-{{@row.id}}"
      {{didInsert this.onInsert}}
    >
      <PrismComboSelectInput
        {{! @glint-expect-error: Incorrect type }}
        @onChange={{this.setDropdownOption}}
        {{! @glint-expect-error: Incorrect type }}
        @onOpen={{this.onOpen}}
        {{! @glint-expect-error: Incorrect type }}
        @options={{this.dropdownOptions}}
        @unsafe_triggerComponent={{GeneralDropdownsTriggersTriggerPowerSelectMeatballs}}
        @value={{null}}
      />
      {{! @glint-expect-error: Incorrect type }}
      {{#if this.issue.isBaitAndSwitchIssue}}
        <button
          class="button--unstyled mr-200"
          title={{t "parity_manager.export_issue_pdf"}}
          type="button"
          {{on "click" this.exportPDF}}
        >
          <PrismIcon @name="download" @size="small" />
        </button>
      {{else if (can "market.paritymanager.email.hotel.send")}}
        {{! @glint-expect-error: Incorrect type }}
        {{#if this.issue.isMailSentToHotel}}
          <button
            class="button--unstyled"
            title={{t "parity_manager.contacted_hotel"}}
            type="button"
            {{on "click" this.transitionToHotelMail}}
          >
            <PrismIcon @name="mail-sent" @size="small" />
          </button>
        {{else}}
          <button
            class="button--unstyled"
            title={{t "parity_manager.contact_hotel"}}
            type="button"
            {{on "click" this.transitionToHotelMail}}
          >
            <PrismIcon @name="mail" @size="small" />
          </button>
        {{/if}}
      {{/if}}
      <span class="propagate-to-drawer" title={{t "general.open_details"}}>
        <PrismIcon @name="arrow-right" @size="small" />
      </span>
    </div>
  </template>
}
